import { Box, Button, LoadingWheel, Stack, Text } from "@vinomofo/components";
import { Field, Form, Formik } from "formik";
import styled, { keyframes } from "styled-components";
import { useCallback, useEffect, useRef, useState } from "react";

import EmailSuggestion from "../email-suggestion";
import { TextField } from "../form-inputs/FormFields";
import emailSpellChecker from "@zootools/email-spell-checker";
import isBrowser from "../../utils/is-browser";
import { isValidEmail } from "../../utils/validate-email";
import {
  BellIcon as BellVinoIcon,
  BellRungIcon as BellRungVinoIcon,
} from "@vinomofo/icons";
import React from "react";

interface Props {
  customerId?: string | undefined;
  active?: boolean;
  onSubscribe: (id?: string) => void;
  onUnsubscribe: () => void;
  loading?: boolean;
}

// Define animations
const ringAnimation = keyframes`
  0% { transform: rotate(0deg); }
  20% { transform: rotate(-15deg); }
  40% { transform: rotate(15deg); }
  60% { transform: rotate(-10deg); }
  80% { transform: rotate(10deg); }
  100% { transform: rotate(0deg); }
`;

const zoomAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const StyledButton = styled(Button)<{
  disabled?: boolean;
  subscribed?: boolean;
}>`
  display: flex;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.2 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  width: 100%;
  height: 48px;
  align-items: center;
  background-color: ${(props) => (props.subscribed ? "#48a90f" : "#ffffff")};
  border: 1px solid #48a90f;
  color: #48a90f;
  transition: background-color 0.3s ease;
  > div {
    width: 36px;
    height: 30px;
    background-color: white;
    border-radius: 50%;
    background-color: #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    position: relative;
    margin-right: 32px;
    .icon-wrapper svg {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      margin: auto;
      opacity: 0;
      transition: opacity 0.5s ease;
      fill: #48a90f;
    }

    &.show-bell .icon-wrapper.bell svg {
      opacity: 1;
      animation: ${zoomAnimation} 0.5s ease, ${ringAnimation} 0.6s ease;
    }

    &.show-bell-rung .icon-wrapper.bell-rung svg {
      opacity: 1;
    }
  }

  &:hover,
  &:active,
  &:focus {
    background-color: ${(props) => (props.subscribed ? "#5AB126" : "#FBFBFB")};
    border-color: #64bf2d;
    span {
      color: ${(props) => (props.subscribed ? "#ffffff" : "#64BF2D")};
    }
    svg {
      fill: ${(props) => (props.disabled ? "#48A90F" : "#64BF2D")};
    }
  }

  &:disabled span {
    color: ${(props) => (props.subscribed ? "#595959" : "#595959")};
  }

  span {
    width: 100%;
    text-align: center;
    margin-left: ${(props) => (props.subscribed ? "-30px" : "-20%")};
    color: ${(props) => (props.subscribed ? "#ffffff" : "#5ab126")};
    line-height: 1.1;
    font-weight: 600;
    transition: color 0.3s ease;
  }
`;

const StyledInput = styled(TextField)`
  background-color: #e9e9e9;
  padding: 8px 15px;
  border: none;
  fontsize: 14px !important;
  width: 100%;
  transition: background-color 0.3s ease;

  &:focus {
    background-color: #f7f7f7 !important;
    outline: none !important;
    border-color: #f7f7f7 !important;
  }
`;

export const RemindMe = ({
  customerId,
  active = false,
  onUnsubscribe,
  onSubscribe,
  loading,
}: Props): JSX.Element => {
  const formRef = useRef();
  const [isSubscribed, setIsSubscribed] = useState(active);
  const [suggestion, setSuggestion] = useState(null);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [debouncedInputValue, setDebouncedInputValue] = useState(null);
  const isGuest = !customerId;
  const textMessage = isSubscribed
    ? null
    : isGuest
    ? "Enter your email below to be notified when this product is back in stock."
    : "Get notified when this product is back in stock.";

  const buttonText = isSubscribed
    ? "You'll be the first to know!"
    : isGuest
    ? "Let me know"
    : "Notify me";

  const onSubmit = useCallback(
    (values: { email?: string }) => {
      setIsBtnLoading(true);
      setSuggestion(null);
      setTimeout(() => {
        setIsBtnLoading(false);

        if (isSubscribed) {
          onUnsubscribe();
        } else {
          if (isGuest) {
            if (isBrowser()) localStorage.setItem("favouriteId", values.email);
            onSubscribe(values.email);
          } else {
            onSubscribe();
          }
        }

        setIsSubscribed((prev) => !prev);
      }, 1500);
    },
    [isSubscribed, onSubscribe, onUnsubscribe, isBtnLoading]
  );

  useEffect(() => {
    setIsSubscribed(active);
  }, [active]);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      setDebouncedInputValue(suggestion?.full);
    }, 1000);
    return () => clearTimeout(delayInputTimeoutId);
  }, [suggestion, 100]);

  useEffect(() => {
    if (isGuest && formRef && formRef.current && !isSubscribed) {
      formRef.current?.resetForm();
    }
  }, [isGuest, formRef, isSubscribed]);

  return (
    <>
      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          padding={1}
        >
          <LoadingWheel borderWidth="5px" size="6em" />
        </Box>
      ) : (
        <Box maxWidth="350px" margin={["0 auto 20px", "auto", "auto"]}>
          {
            <Formik
              innerRef={formRef}
              initialValues={{ email: "" }}
              enableReinitialize
              validate={(values) => {
                if (isSubscribed) return {};
                setSuggestion(null);
                const suggestion = emailSpellChecker.run({
                  email: values.email,
                });
                if (suggestion) {
                  setSuggestion(suggestion);
                }
                const errors: { email?: string } = {};

                if (!values.email) {
                  errors.email = "Please enter your email address";
                } else if (!suggestion && !isValidEmail(values.email)) {
                  errors.email = "Invalid email address";
                }

                return errors;
              }}
              onSubmit={onSubmit}
              validateOnChange
            >
              {({ setFieldValue, values }) => (
                <Form>
                  <Stack space={1}>
                    {textMessage && (
                      <Box>
                        <Text as="strong" fontSize="12px" fontWeight="600">
                          {textMessage}
                        </Text>
                      </Box>
                    )}
                    {isGuest && !isSubscribed && (
                      <Box>
                        <Field
                          name="email"
                          placeholder="Email Address"
                          component={StyledInput}
                          defaultValue={values.email}
                        />
                      </Box>
                    )}
                    {debouncedInputValue && (
                      <Box flex="100%">
                        <EmailSuggestion
                          style={{
                            marginTop: -20,
                            zIndex: 2,
                            position: "relative",
                          }}
                          onAccept={() =>
                            setFieldValue("email", suggestion?.full)
                          }
                          onIgnore={() => {
                            const to = setTimeout(() => {
                              setDebouncedInputValue(null);
                              clearTimeout(to);
                            }, 100);
                          }}
                          text={
                            <>
                              Did you mean{" "}
                              <strong color="black">
                                {debouncedInputValue}
                              </strong>{" "}
                              ?
                            </>
                          }
                        />
                      </Box>
                    )}
                    <Box
                      mt={
                        debouncedInputValue
                          ? "5px"
                          : !isGuest
                          ? "0"
                          : isSubscribed
                          ? "0"
                          : "-12px"
                      }
                    >
                      <StyledButton
                        variant="primary"
                        py="10px"
                        px="12px"
                        type={isGuest ? "submit" : undefined}
                        onClick={!isGuest ? onSubmit : undefined}
                        subscribed={isSubscribed}
                        disabled={isBtnLoading || isSubscribed}
                      >
                        <Box
                          className={`${
                            isSubscribed ? "show-bell" : "show-bell-rung"
                          }`}
                        >
                          <div className="icon-wrapper bell">
                            {React.cloneElement(<BellRungVinoIcon />, {
                              size: "18px",
                            })}
                          </div>
                          <div className="icon-wrapper bell-rung">
                            {React.cloneElement(<BellVinoIcon />, {
                              size: "18px",
                            })}
                          </div>
                        </Box>
                        <Text as="span" fontSize={["14px", "16px"]}>
                          {isBtnLoading
                            ? !isSubscribed
                              ? "Submitting..."
                              : "Removing Reminder..."
                            : buttonText}
                        </Text>
                      </StyledButton>
                    </Box>
                  </Stack>
                </Form>
              )}
            </Formik>
          }
        </Box>
      )}
    </>
  );
};
